import { onMounted, ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import TextField from 'src/components/form/text-field/index.vue'
import i18n from 'src/assets/i18n/i18n'
import { cloneDeep, isEmpty, isEqual, isNumber } from 'lodash'
import { useForm } from 'vee-validate'
import Misc from 'src/utils/helper/misc'
import { MdTypography, MdSpinner } from '@antiqua-dls/vue-components'

import TooltipError from '../../tooltip-error/TooltipError.vue'

export default {
  name: 'BundlePrice',
  props: {
    bundleFormData: {
      type: Object,
      required: true
    },
    fontsCount: {
      type: Number,
      required: true
    },
    modelValue: {
      type: Array,
      required: true
    }
  },
  components: {
    TextField,
    MdSpinner,
    TooltipError,
    MdTypography
  },
  setup(props, { emit }) {
    const priceValue = ref(100)
    const prices_validation_messages = [
      'Cannot be 0',
      'Cannot be a negative number',
      'Cannot be non-numeric value',
      'Cannot be more than 10,000',
      'Cannot have more than two decimal in place'
    ]

    const BUNDLE_PRICE_CONSTANTS = {
      USD: i18n().global.t('pages.bundle.draft.bundle_price.country_codes.USD'),
      comma_separation_max_count: 3,
      min_fonts_count: 2,
      zero_value: 0,
      warning_price: 9,
      max_truncate_string_length: 50,
      prices_validation_messages,
      prices_usd_validation_messages: [
        'Cannot be empty',
        ...prices_validation_messages
      ],
      MSRP: 'MSRP',
    }

    const countryCodes = ref([
      { acronym: 'EUR', symbol: '€' },
      { acronym: 'AUD', symbol: '$' },
      { acronym: 'CAD', symbol: '$' },
      { acronym: 'GBP', symbol: '£' },
      { acronym: 'BRL', symbol: 'R$' },
      { acronym: 'JPY', symbol: '¥' },
      { acronym: 'NZD', symbol: '$' },
      { acronym: 'CNY', symbol: '¥' },
      { acronym: 'KRW', symbol: '₩' }
    ])

    const isCurrencyLoading = ref(true)

    onMounted(async () => {
      await getCurrencyConversions().finally(() => {
        isCurrencyLoading.value = false
      })

      if (!isEmpty(props.bundleFormData.prices)) {
        usdPrice.value = props.bundleFormData.prices
          .find(bundle => isEqual(bundle.currency, BUNDLE_PRICE_CONSTANTS.USD))
          ?.price?.toFixed(2)
      } else {
        usdPrice.value = null
        writableModelValue.value = []
      }

      countryCodes.value.forEach(countryCode => {
        const foundCurrency = props.bundleFormData.prices?.find(bundle => isEqual(bundle.currency, countryCode.acronym))
        if (foundCurrency) {
          countryCode.price = foundCurrency.price?.toFixed(2)
        }
      })

      usdPriceUpdateModelValue()
    })

    const store = useStore()

    const currencies = computed(() => store.state.packages.conversions)

    const commaSeparatedFoundries = computed(() =>
      props?.bundleFormData?.foundries?.map(foundry => foundry?.label).join(', ')
    )

    const foundriesCount = computed(() => props.bundleFormData.foundries?.length)

    const selectedFoundries = computed(() => {
      return foundriesCount.value <= BUNDLE_PRICE_CONSTANTS.comma_separation_max_count
        ? commaSeparatedFoundries.value
        : `${foundriesCount.value} Foundries`
    })

    const priceWarning = ref({
      show: false,
      message: ''
    })

    const placeHolderUSDPrice = ref(100)

    const convertedPrice = computed(() => {
      const convertedPricePayload = {}

      countryCodes.value.forEach(countryCode => {
        const rate = getConvertedRate()
        convertedPricePayload[countryCode.acronym] = getConvertedPrice(countryCode.acronym, rate)
      })

      return convertedPricePayload
    })

    function getConvertedRate() {
      if (isNumber(numberedPrice.value) && numberedPrice.value > BUNDLE_PRICE_CONSTANTS.zero_value) {
        priceValue.value = numberedPrice.value
        return numberedPrice.value
      } else {
        return priceValue.value
      }
    }

    const usdPrice = ref(null)

    const numberedPrice = computed(() => Number(usdPrice.value))

    function usdPriceUpdateModelValue() {
      if (numberedPrice.value <= 0) return

      if (numberedPrice.value <= BUNDLE_PRICE_CONSTANTS.warning_price) {
        priceWarning.value.show = true
        priceWarning.value.message = i18n().global.t('pages.bundle.draft.bundle_price.warning')
      } else {
        priceWarning.value.show = false
        priceWarning.value.message = null
      }
    }

    /**
     * Clear Prices Section
     */

    watch(
      () => props.bundleFormData.prices,
      newPrices => {
        if (isEmpty(newPrices)) {
          usdPrice.value = null
          countryCodes.value = countryCodes.value.map(country => {
            return {
              ...country,
              price: ''
            }
          })
        }
      }
    )

    /**
     * getConvertedPrice
     * @param {String} countryCode
     * @param {Number} price
     * @returns {String}
     **/

    function getConvertedPrice(countryCode, rate = 1) {
      if (!isNumber(rate)) return

      const foundCurrency = currencies.value.find(curr => isEqual(curr.currency, countryCode))
      let convertedCurrency = foundCurrency ? rate * foundCurrency.rate : rate

      convertedCurrency = convertedCurrency.toFixed(2)

      return convertedCurrency
    }

    /**
     * getCurrencyConversions
     * @returns {Promise}
     */

    function getCurrencyConversions() {
      return store.dispatch('packages/getCurrencyConversions')
    }

    const writableModelValue = computed({
      get: () => props.modelValue,
      set: newPrices => emit('update:model-value', newPrices)
    })

    const { validate, validateField, errors } = useForm()

    const usdPriceField = i18n().global.t('pages.bundle.draft.bundle_price.$')

    function onUSDPriceBlur(currencyPrice) {
      // if currency is USD and price is empty, return
      emit('priceError', errors.value)
      if (isEqual(currencyPrice.currency, BUNDLE_PRICE_CONSTANTS.USD) && !currencyPrice.price) return

      writableModelValue.value = writableModelValue.value.filter(
        curr => !isEqual(curr.currency, currencyPrice.currency)
      )

      let foundCurrency = writableModelValue.value?.find(curr => isEqual(curr.currency, currencyPrice.currency))

      if (isEqual(currencyPrice.currency, BUNDLE_PRICE_CONSTANTS.USD)) {
        emit('validate', () => validateField(usdPriceField))
      } else {
        emit('validate', validate)
      }

      if (isEmpty(foundCurrency)) {
        writableModelValue.value = [...writableModelValue.value, currencyPrice]
      } else {
        foundCurrency.price = currencyPrice.price
        writableModelValue.value = cloneDeep(writableModelValue.value)
      }
    }

    return {
      usdPrice,
      usdPriceUpdateModelValue,
      currencies,
      isCurrencyLoading,
      getConvertedRate,
      getConvertedPrice,
      priceWarning,
      convertedPrice,
      countryCodes,
      priceValue,
      numberedPrice,
      writableModelValue,
      onUSDPriceBlur,
      placeHolderUSDPrice,
      selectedFoundries,
      commaSeparatedFoundries,
      foundriesCount,
      usdPriceField,
      BUNDLE_PRICE_CONSTANTS,
      errors,
      Misc,
      validate
    }
  }
}
